import React, { useState } from 'react';
import useAudioQuality from '../hooks/useAudioQuality';

const AudioQualitySettings = () => {
  const {
    audioUseCase,
    participantCount,
    noiseSuppressionEnabled,
    echoCancellationEnabled,
    autoGainControlEnabled,
    autoOptimizationEnabled,
    audioLevel,
    backgroundNoiseLevel,
    recommendedConfig,
    setAudioUseCase,
    setAutoOptimizationEnabled,
    toggleNoiseSuppression,
    toggleEchoCancellation,
    toggleAutoGainControl,
    isHighNoiseEnvironment,
    isLargeMeeting,
    isMusicMode,
    isHighQualityMode
  } = useAudioQuality();

  const [showSettings, setShowSettings] = useState(false);

  const useCaseOptions = [
    {
      value: 'meeting',
      label: 'Meeting/Conference',
      description: 'Optimized for speech clarity',
      icon: '🎤'
    },
    {
      value: 'music',
      label: 'Music Sharing',
      description: 'Preserves musical elements',
      icon: '🎵'
    },
    {
      value: 'highQuality',
      label: 'High Quality',
      description: 'Best audio quality available',
      icon: '🔊'
    },
    {
      value: 'lowBandwidth',
      label: 'Low Bandwidth',
      description: 'Optimized for poor connections',
      icon: '📶'
    }
  ];

  const getAudioLevelColor = () => {
    if (audioLevel < 20) return 'bg-gray-300';
    if (audioLevel < 50) return 'bg-green-500';
    if (audioLevel < 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getBackgroundNoiseIcon = () => {
    switch (backgroundNoiseLevel) {
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const ToggleSwitch = ({ enabled, onToggle, label, description }) => (
    <div className="flex items-center justify-between py-2">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-900">{label}</label>
        {description && (
          <p className="text-xs text-gray-600">{description}</p>
        )}
      </div>
      <button
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="relative">
      {/* Audio Quality Indicator Button */}
      <button
        onClick={() => setShowSettings(!showSettings)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        title="Audio Quality Settings"
      >
        <span className="text-sm">🎧</span>
        <span className="text-xs font-medium">
          {useCaseOptions.find(opt => opt.value === audioUseCase)?.icon}
        </span>
        <span className="text-xs">{getBackgroundNoiseIcon()}</span>
        
        {/* Audio Level Indicator */}
        <div className="w-6 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${getAudioLevelColor()}`}
            style={{ width: `${Math.min(audioLevel, 100)}%` }}
          />
        </div>
      </button>

      {/* Settings Panel */}
      {showSettings && (
        <div className="absolute bottom-full mb-2 right-0 w-96 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
          <div className="space-y-4">
            {/* Status Information */}
            <div className="border-b border-gray-200 pb-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Audio Quality Status</h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Participants:</span>
                  <span className="font-medium ml-1">{participantCount}</span>
                  {isLargeMeeting && <span className="text-orange-600 ml-1">📢</span>}
                </div>
                <div>
                  <span className="text-gray-600">Noise Level:</span>
                  <span className="font-medium ml-1">
                    {getBackgroundNoiseIcon()} {backgroundNoiseLevel}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Mode:</span>
                  <span className="font-medium ml-1">
                    {useCaseOptions.find(opt => opt.value === audioUseCase)?.icon}
                    {' '}{audioUseCase}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Audio Level:</span>
                  <span className="font-medium ml-1">{Math.round(audioLevel)}%</span>
                </div>
              </div>
            </div>

            {/* Auto Optimization Toggle */}
            <ToggleSwitch
              enabled={autoOptimizationEnabled}
              onToggle={() => setAutoOptimizationEnabled(!autoOptimizationEnabled)}
              label="Auto Audio Optimization"
              description="Automatically adjusts settings based on conditions"
            />

            {/* Use Case Selection */}
            <div>
              <label className="text-sm font-medium text-gray-900 mb-2 block">
                Audio Use Case
              </label>
              <div className="space-y-2">
                {useCaseOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setAudioUseCase(option.value)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      audioUseCase === option.value
                        ? 'bg-blue-50 border-blue-300 text-blue-900'
                        : 'hover:bg-gray-100 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{option.icon}</span>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className="text-xs text-gray-600">{option.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Manual Audio Controls */}
            {!autoOptimizationEnabled && (
              <div className="border-t border-gray-200 pt-3">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Manual Audio Controls</h4>
                <div className="space-y-3">
                  <ToggleSwitch
                    enabled={noiseSuppressionEnabled}
                    onToggle={toggleNoiseSuppression}
                    label="Noise Suppression"
                    description="Reduces background noise"
                  />
                  
                  <ToggleSwitch
                    enabled={echoCancellationEnabled}
                    onToggle={toggleEchoCancellation}
                    label="Echo Cancellation"
                    description="Removes audio echoes"
                  />
                  
                  <ToggleSwitch
                    enabled={autoGainControlEnabled}
                    onToggle={toggleAutoGainControl}
                    label="Auto Gain Control"
                    description="Maintains consistent audio levels"
                  />
                </div>
              </div>
            )}

            {/* Current Configuration Display */}
            {recommendedConfig && (
              <div className="border-t border-gray-200 pt-3">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Configuration</h4>
                <div className="bg-gray-50 p-3 rounded-md">
                  <div className="text-xs space-y-1">
                    <div>
                      <span className="text-gray-600">Encoder:</span>
                      <span className="font-medium ml-1">{recommendedConfig.encoderConfig}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Noise Suppression:</span>
                      <span className="font-medium ml-1">
                        {recommendedConfig.noiseConfig?.noiseSuppression ? '✅' : '❌'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Echo Cancellation:</span>
                      <span className="font-medium ml-1">
                        {recommendedConfig.noiseConfig?.echoCancellation ? '✅' : '❌'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Auto Gain Control:</span>
                      <span className="font-medium ml-1">
                        {recommendedConfig.noiseConfig?.autoGainControl ? '✅' : '❌'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Environment Warnings */}
            <div className="border-t border-gray-200 pt-3">
              <div className="text-xs space-y-2">
                {isHighNoiseEnvironment && (
                  <div className="flex items-center space-x-2 text-orange-600 bg-orange-50 p-2 rounded">
                    <span>⚠️</span>
                    <span>High background noise detected. Consider using headphones.</span>
                  </div>
                )}
                
                {isLargeMeeting && (
                  <div className="flex items-center space-x-2 text-blue-600 bg-blue-50 p-2 rounded">
                    <span>📢</span>
                    <span>Large meeting detected. Enhanced noise suppression active.</span>
                  </div>
                )}
                
                {isMusicMode && (
                  <div className="flex items-center space-x-2 text-purple-600 bg-purple-50 p-2 rounded">
                    <span>🎵</span>
                    <span>Music mode active. Audio processing optimized for musical content.</span>
                  </div>
                )}
                
                {isHighQualityMode && (
                  <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-2 rounded">
                    <span>🔊</span>
                    <span>High quality mode active. Using maximum audio fidelity.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioQualitySettings;